
import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  serverTimestamp,
  setDoc,
} from "firebase/firestore";

import { auth, db } from "@/firebase";

function cleanString(value) {
  return String(value ?? "").trim();
}

function normaliseSalesUnits(value) {
  const number = Number(value);

  if (!Number.isFinite(number) || number <= 0) {
    return 0;
  }

  return Math.round(
    (number + Number.EPSILON) * 10000
  ) / 10000;
}

function normaliseIngredients(ingredients) {
  const merged = new Map();

  for (
    const item of Array.isArray(ingredients)
      ? ingredients
      : []
  ) {
    const product_key = cleanString(item?.product_key);
    const product_name = cleanString(item?.product_name);
    const product_group = cleanString(item?.product_group);

    const selling_units = normaliseSalesUnits(
      item?.selling_units
    );

    if (
      !product_key ||
      !product_name ||
      !product_group ||
      !selling_units
    ) {
      continue;
    }

    const previous = merged.get(product_key);

    if (previous) {
      previous.selling_units = normaliseSalesUnits(
        previous.selling_units + selling_units
      );
    } else {
      merged.set(product_key, {
        product_key,
        product_name,
        product_group,
        selling_units,
      });
    }
  }

  return [...merged.values()];
}

function normaliseRecipeComponents(components) {
  return (
    Array.isArray(components)
      ? components
      : []
  )
    .map((component) => ({
      recipe_id: cleanString(component?.recipe_id),
      recipe_name: cleanString(component?.recipe_name),
      quantity: normaliseSalesUnits(component?.quantity),
    }))
    .filter(
      (component) =>
        component.recipe_id &&
        component.quantity > 0
    );
}

/**
 * Resolves included recipes into actual stock ingredients.
 *
 * Existing stock counts continue receiving the same
 * flattened ingredient structure as before.
 *
 * If a source recipe changes, its dependent recipes
 * are recalculated whenever recipes are fetched.
 */
function resolveRecipeIngredients(recipes) {
  const byId = new Map(
    recipes.map((recipe) => [
      recipe.id,
      recipe,
    ])
  );

  const cache = new Map();

  function resolve(
    recipeId,
    visiting = new Set()
  ) {
    if (cache.has(recipeId)) {
      return cache.get(recipeId);
    }

    const recipe = byId.get(recipeId);

    if (!recipe) {
      throw new Error(
        `An included stock recipe no longer exists: ${recipeId}`
      );
    }

    if (visiting.has(recipeId)) {
      throw new Error(
        `Circular stock recipe reference: ${recipe.recipe_name}`
      );
    }

    const next = new Set(visiting);
    next.add(recipeId);

    // Existing recipes have only ingredients.
    // Composite recipes also store their manually
    // added ingredients separately.
    const base = Array.isArray(
      recipe.base_ingredients
    )
      ? recipe.base_ingredients
      : recipe.ingredients;

    const parts = [...base];

    for (const component of recipe.recipe_components) {
      const nested = resolve(
        component.recipe_id,
        next
      );

      for (const ingredient of nested) {
        parts.push({
          ...ingredient,

          selling_units:
            ingredient.selling_units *
            component.quantity,
        });
      }
    }

    const flattened = normaliseIngredients(parts);

    cache.set(recipeId, flattened);

    return flattened;
  }

  return recipes.map((recipe) => ({
    ...recipe,

    ingredients: resolve(recipe.id),
  }));
}

export async function fetchStockRecipes() {
  const snapshot = await getDocs(
    collection(db, "stock_recipes")
  );

  const recipes = snapshot.docs.map(
    (recipeDocument) => {
      const data = recipeDocument.data() || {};

      return {
        id: recipeDocument.id,
        ...data,

        ingredients: normaliseIngredients(
          data.ingredients
        ),

        base_ingredients: Array.isArray(
          data.base_ingredients
        )
          ? normaliseIngredients(
              data.base_ingredients
            )
          : undefined,

        recipe_components: normaliseRecipeComponents(
          data.recipe_components
        ),
      };
    }
  );

  return resolveRecipeIngredients(recipes).sort(
    (a, b) =>
      cleanString(a.recipe_name).localeCompare(
        cleanString(b.recipe_name),
        "en",
        {
          sensitivity: "base",
        }
      )
  );
}

export async function saveStockRecipe({
  recipeId = "",
  recipeName,
  ingredients,
  baseIngredients = undefined,
  recipeComponents = undefined,
}) {
  const user = auth.currentUser;

  if (!user) {
    throw new Error(
      "You must be signed in to save a recipe."
    );
  }

  const name = cleanString(recipeName);

  const flattened = normaliseIngredients(
    ingredients
  );

  if (!name) {
    throw new Error(
      "Recipe name is required."
    );
  }

  if (!flattened.length) {
    throw new Error(
      "Add at least one valid ingredient or included recipe."
    );
  }

  const existingId = cleanString(recipeId);

  const recipeReference = existingId
    ? doc(db, "stock_recipes", existingId)
    : doc(collection(db, "stock_recipes"));

  const payload = {
    recipe_name: name,

    recipe_name_lower:
      name.toLocaleLowerCase("en-GB"),

    ingredients: flattened,

    updated_at: serverTimestamp(),
    updated_by_uid: user.uid,
    updated_by_email: user.email || "",
  };

  if (baseIngredients !== undefined) {
    payload.base_ingredients =
      normaliseIngredients(baseIngredients);
  }

  if (recipeComponents !== undefined) {
    payload.recipe_components =
      normaliseRecipeComponents(recipeComponents);
  }

  if (!existingId) {
    payload.created_at = serverTimestamp();
    payload.created_by_uid = user.uid;
    payload.created_by_email = user.email || "";
  }

  await setDoc(
    recipeReference,
    payload,
    { merge: true }
  );

  return recipeReference.id;
}

export async function deleteStockRecipe(recipeId) {
  const user = auth.currentUser;
  const id = cleanString(recipeId);

  if (!user) {
    throw new Error(
      "You must be signed in to delete a recipe."
    );
  }

  if (!id) {
    throw new Error(
      "Missing recipe ID."
    );
  }

  await deleteDoc(
    doc(db, "stock_recipes", id)
  );
}
